<template>
  <div class="relative inline-block">
    <Button
        @click.stop="togglePopup"
        class="bg-primary text-white px-4 py-2 rounded-lg shadow hover:bg-primary/90 whitespace-nowrap w-full flex items-center justify-center"
    >
      <span>{{ buttonText || __('Add to') }}</span>
      <Icon
          v-if="showIcon"
          :name="iconName"
          :class="iconClass"
      />
    </Button>

    <transition
        enter-active-class="transition-all duration-150 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-all duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
    >
      <div
          v-if="props.isOpen"
          @click.stop
          class="absolute z-50 h-40 overflow-y-auto overscroll-contain scrollbar-thin rounded-lg border border-gray-200 bg-white text-black shadow-lg"
          :class="popupPositionClasses"
      >
        <div
            v-if="isLoading"
            class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white bg-opacity-95"
        >
          <div
              class="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary"
          ></div>
        </div>

        <ul v-if="options.length" class="flex flex-col text-sm text-gray-700">
          <li v-for="option in options" :key="option.id">
            <button
                @click.stop="selectOption(option)"
                class="block w-full cursor-pointer rounded px-3.5 py-2 text-left transition-colors duration-150 hover:bg-gray-100"
            >
              <div class="flex h-full w-full items-center justify-start">
                <span
                    class="w-60 overflow-hidden text-ellipsis whitespace-nowrap text-left text-base font-medium"
                >
                  {{ option.name }}
                </span>
              </div>
            </button>
          </li>
        </ul>

        <div
            v-else
            class="flex h-full w-60 items-center justify-center p-4 text-center text-gray-500"
        >
          <span>
            {{ __('No options available') }}
          </span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Button } from '@/components/block/button';
import { Icon } from '@/components/block/icon';
import { __ } from '@/helpers';

const props = defineProps({
  options: {
    type: Array,
    required: true,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  buttonText: {
    type: String,
    default: null
  },
  showIcon: {
    type: Boolean,
    default: false
  },
  iconName: {
    type: String,
    default: 'keyboard_arrow_down'
  },
  iconClass: {
    type: String,
    default: 'w-4 h-4 text-white text-xl ml-2'
  },
  popupPosition: {
    type: String,
    default: 'bottom',
    validator: (value) => ['top', 'bottom'].includes(value)
  },
  popupAlignment: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right', 'center'].includes(value)
  },
  isOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['select-option', 'toggle']);

const popupPositionClasses = computed(() => {
  const classes = [];

  if (props.popupPosition === 'bottom') {
    classes.push('bottom-full', 'mb-2');
  } else {
    classes.push('top-full', 'mt-2');
  }

  if (props.popupAlignment === 'left') {
    classes.push('left-0');
  } else if (props.popupAlignment === 'center') {
    classes.push('left-1/2', '-translate-x-1/2');
  } else {
    classes.push('right-0');
  }

  return classes.join(' ');
});

const togglePopup = () => {
  emit('toggle');
};

const selectOption = (option) => {
  emit('select-option', option);
};

const handleClickOutside = (event) => {
  const clickedElement = event.target;
  const popupElement = clickedElement.closest('.relative.inline-block');

  if (!popupElement && props.isOpen) {
    emit('toggle');
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>